/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

import { AssistantContext } from '@/contexts';
import type { AssistantContextType } from '@/contexts/AssistantContext';
import { DEFAULT_INDIVIDU } from '@/constants';
import type { Individu } from '@/types';

import parseHubeeDemandeur from './AssistantHubeeContext/parseHubeeDemandeur';

type AssistantContextProviderProps = {
  children: React.ReactNode;
};

const AssistantContextProvider = ({
  children,
}: AssistantContextProviderProps) => {
  const [individu, setIndividu] = React.useState<Individu>(DEFAULT_INDIVIDU);
  const [errors, setErrors] = React.useState<any>({});
  const [teledossierData, setTeledossierData] = React.useState<any>(null);
  const [isTeledossierDataApplied, setIsTeledossierDataApplied] =
    React.useState<boolean>(false);
  const [isLoadingHubeeData, setIsLoadingHubeeData] =
    React.useState<boolean>(false);

  const { jourNaissance, moisNaissance, anneeNaissance } = individu || {};

  const handleIndividuChange = React.useCallback(
    (name: string, value: string | number | boolean) => {
      setIndividu((prev: Individu) => {
        const newIndividu = _.cloneDeep(prev);

        _.set(newIndividu, name, value);

        return newIndividu;
      });
    },
    [],
  );

  const handleChange = React.useCallback(
    (_e, { name, value, checked }) => {
      handleIndividuChange(name, checked ?? value);
    },
    [handleIndividuChange],
  );

  React.useEffect(() => {
    setIndividu({ ...DEFAULT_INDIVIDU });
  }, []);

  const handleReset = React.useCallback(() => {
    setIndividu({ ...DEFAULT_INDIVIDU });
    setErrors([]);
  }, []);

  // Effect pour initialiser les valeurs par défaut depuis teledossierData
  React.useEffect(() => {
    // On applique les données seulement si :
    // On a des données de teledossier
    // On n'a pas déjà appliqué les données
    if (!teledossierData || isTeledossierDataApplied) {
      return;
    }

    // Cas 1: Un brouillon existant a été chargé (données de l'individu directement)
    if (teledossierData.isExistingBrouillon && teledossierData.individu) {
      const brouillonData = teledossierData.individu;
      setIsLoadingHubeeData(true);

      // S'assurer que parent1 et parent2 sont des objets (pas null)
      const safeParent = (parent: any) => parent || {};

      const formatPrenomsComplementaires = (
        prenoms: string | null | undefined,
      ) => {
        if (!prenoms) return '';
        return prenoms
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean)
          .join(', ');
      };

      const { adresseNaissance } = brouillonData;
      const { adresseRattachement } = brouillonData;
      const { adresseContact } = brouillonData;

      const formattedPrenoms = formatPrenomsComplementaires(
        brouillonData.prenomsComplementaires,
      );

      setIndividu((prev) => ({
        ...prev,
        ...brouillonData,
        prenomsComplementaires: formattedPrenoms,
        adresseNaissance,
        adresseRattachement,
        adresseContact,
        parent1: safeParent(brouillonData.parent1),
        parent2: safeParent(brouillonData.parent2),
        hasDoubleNationalite: !_.isNil(brouillonData.identifiantNationaliteBis),
      }));
      setIsTeledossierDataApplied(true);
      setIsLoadingHubeeData(false);

      return;
    }

    // Cas 2: Nouvelles données de télédossier Hubee (format structuré)
    if (teledossierData?.demandeur) {
      setIsLoadingHubeeData(true);

      const updates = parseHubeeDemandeur(teledossierData, individu);

      // On applique les mises à jour s'il y en a
      if (Object.keys(updates).length > 0) {
        setIndividu((prev) => ({
          ...prev,
          ...updates,
        }));
      }

      setIsTeledossierDataApplied(true);
      setIsLoadingHubeeData(false);
    }
  }, [teledossierData, individu, isTeledossierDataApplied]);

  const context = React.useMemo<AssistantContextType>(
    () => ({
      ...individu,
      errors,
      setErrors,
      onChange: handleChange,
      onReset: handleReset,
      teledossierData,
      setTeledossierData,
      isLoadingHubeeData,
      setIsLoadingHubeeData,
    }),
    [
      individu,
      errors,
      handleChange,
      handleReset,
      teledossierData,
      isLoadingHubeeData,
    ],
  );

  React.useEffect(() => {
    const birthDate = dayjs(
      `${jourNaissance}/${moisNaissance}/${anneeNaissance}`,
      'D/M/YYYY',
    );

    // Les différents âges nécessaires pour le recensement
    const vingtCinqAns = dayjs().subtract(25, 'years');
    const seizeAns = dayjs().subtract(16, 'year');
    const seizeAnsTroisMois = dayjs().subtract(16, 'year').subtract(3, 'month');

    // Les booléens
    const nonRecense = birthDate.isBefore(vingtCinqAns);
    const recense = birthDate.isBetween(seizeAns, seizeAnsTroisMois);
    const recenseRegularise = birthDate.isBetween(
      seizeAnsTroisMois,
      vingtCinqAns,
    );

    if (recense) {
      handleChange(null, {
        name: 'recensement',
        value: 'recenses',
        checked: null,
      });
    } else if (recenseRegularise) {
      handleChange(null, {
        name: 'recensement',
        value: 'recenses-regularises',
        checked: null,
      });
    } else if (nonRecense) {
      handleChange(null, {
        name: 'recensement',
        value: 'non-recenses',
        checked: null,
      });
    }

    // Initialiser la date de recensement seulement si elle n'est pas déjà définie
    setIndividu((prevIndividu) => {
      // Si les valeurs de recensement sont déjà définies (différentes de DEFAULT_INDIVIDU),
      // on ne les écrase pas
      if (
        prevIndividu.trimestreRecensement !==
          DEFAULT_INDIVIDU.trimestreRecensement ||
        prevIndividu.anneeRecensement !== DEFAULT_INDIVIDU.anneeRecensement ||
        prevIndividu.dateRecensement !== DEFAULT_INDIVIDU.dateRecensement
      ) {
        return prevIndividu;
      }

      // Sinon, on initialise avec les valeurs actuelles
      return {
        ...prevIndividu,
        trimestreRecensement: Math.ceil((dayjs().month() + 1) / 3),
        anneeRecensement: dayjs().year(),
        dateRecensement: dayjs().format('YYYY-MM-DD'),
      };
    });
  }, [jourNaissance, moisNaissance, anneeNaissance, handleChange]);

  return (
    <AssistantContext.Provider value={context}>
      {children}
    </AssistantContext.Provider>
  );
};

export default AssistantContextProvider;
